import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { handleBasicsChange } from "../../../../Redux/reducers/questionTranslationSlice";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { ChangeEvent, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { builderGetUploadUrl } from "../../../../utils/upload";
import { useParams } from "react-router-dom";
import Add from "@mui/icons-material/Add";
import { Visibility } from "@mui/icons-material";
import MDEditor, { commands } from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import MarkdownPreview from "./MarkdownPreview";
import { DeleteIcon } from "../../Icons";

type Props = {
  question: any;
};

export const requiredStyles: SxProps = {
  "&::after": {
    content: '"*"',
    color: "red",
    marginLeft: "3px",
  },
};

const uploadWrapper: SxProps = {
  width: 200,
  height: 140,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  py: 2,
  px: 4,
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

const previewWrapper: SxProps = {
  width: 200,
  height: 140,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  py: 2,
  px: 4,
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

export const BasicTab = ({ question }: Props) => {
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();

  const isBasicError = useAppSelector(
    ({ questionTranslation }) => questionTranslation.isBasicError
  );
  const isPreview = useAppSelector(
    ({ questionTranslation }) => !questionTranslation.editable
  );
  const formId = useAppSelector(
    ({ questionTranslation }) => questionTranslation.formId
  );
  const formLevel = useAppSelector(
    ({ questionTranslation }) => questionTranslation.formLevel
  );
  const previewUrl = question?.signedImageUrl;

  const [loading, setLoading] = useState(false);
  const isAllowRemark = useAppSelector(
    ({ questionTranslation }) => questionTranslation.mainQuestion?.allowRemark
  );
  const isDisplayNumber = useAppSelector(
    ({ questionTranslation }) => !questionTranslation.mainQuestion?.showIndex
  );

  const [displayMarkdown, setMarkdown] = useState(false);

  const onChange = (
    key: string,
    value: string | boolean,
    isProp?: boolean,
    convertToNum?: boolean
  ) => {
    if (!isPreview)
      dispatch(
        handleBasicsChange({
          key,
          value,
          isProp,
          convertToNum,
        })
      );
  };

  const showMarkdown = () => {
    setMarkdown(true);
  };

  const closeMarkdown = () => {
    setMarkdown(false);
  };

  // const handleAllowRemark = (e: ChangeEvent<HTMLInputElement>) => {
  //   setAllowRemark(e.target.checked);
  //   onChange("remarkText", e.target.checked ? "Enter Remark" : "", true);
  // };

  const handleDisplayNumber = (e: ChangeEvent<HTMLInputElement>) => {
    let isValid = true;
    if (isValid) {
      onChange("displayNumber", e.target.value, true);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const file = acceptedFiles?.[0];
        if (file && studyId) {
          if (file.size > 5 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 5 MB!");
            return;
          }

          setLoading(true);

          const obj = await builderGetUploadUrl(
            file,
            studyId,
            formId,
            formLevel
          );
          if (obj.postUploadImageUrl) {
            dispatch(
              handleBasicsChange({
                key: "imageUrl",
                value: obj.postUploadImageUrl,
              })
            );
          }
          if (obj.previewUrl) {
            dispatch(
              handleBasicsChange({
                key: "signedImageUrl",
                value: obj.previewUrl,
              })
            );
          }

          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [dispatch, studyId, formId, formLevel]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "image/jpg": [],
      "image/jpeg": [],
      "image/png": [],
      // "application/pdf": [],
    },
    disabled: isPreview,
  });

  const removeImage = () => {
    dispatch(handleBasicsChange({ key: "imageUrl", value: null }));
    dispatch(handleBasicsChange({ key: "signedImageUrl", value: null }));
  };

  return (
    <Stack gap={2} py={1}>
      {isBasicError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isBasicError}
        </Typography>
      )}
      <Stack gap={1}>
        {loading ? (
          <Box sx={{ color: "text.secondary" }}>
            <CircularProgress />
          </Box>
        ) : previewUrl ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={"Upload Image"} placement="right">
              <Box
                {...getRootProps({ className: "dropzone" })}
                sx={previewWrapper}
              >
                <input {...getInputProps()} />
                <Avatar
                  src={previewUrl}
                  variant="rounded"
                  sx={{ width: 200, height: 140 }}
                />
              </Box>
            </Tooltip>
            {!isPreview && (
              <IconButton
                color={"error"}
                sx={{ height: 40, width: 40 }}
                onClick={removeImage}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ) : (
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            {...getRootProps({ className: "dropzone" })}
            sx={{ ...uploadWrapper }}
          >
            <input {...getInputProps()} />

            <Stack alignItems={"center"} gap={1}>
              <Add fontSize="medium" sx={{ color: "text.secondary" }} />
              <Typography
                sx={{ color: "text.secondary" }}
                variant="body1"
                fontWeight={"medium"}
                textAlign={"center"}
              >
                Add Image <br />
                <Typography
                  sx={{ color: "text.secondary" }}
                  component={"span"}
                  variant="body1"
                >
                  or drag and drop
                </Typography>
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
      <Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            variant="body1"
            fontWeight={"regular"}
            sx={requiredStyles}
          >
            Label
          </Typography>
          <IconButton onClick={showMarkdown} size="small">
            <Visibility fontSize="small" />
          </IconButton>
        </Stack>

        <div className="container">
          <MDEditor
            data-color-mode="light"
            value={question.label}
            onChange={(value) => {
              onChange("label", value || "");
            }}
            previewOptions={{
              rehypePlugins: [[rehypeSanitize]],
            }}
            textareaProps={{
              placeholder: "Enter text here",
              readOnly: isPreview,
            }}
            extraCommands={[]}
            commands={[
              commands.bold,
              commands.italic,
              commands.strikethrough,
              commands.quote,
              commands.unorderedListCommand,
              commands.orderedListCommand,
            ]}
            visibleDragbar={false}
            preview="edit"
          />
        </div>
      </Stack>

      {!(
        ["statement", "summary"].includes(question.type) && isDisplayNumber
      ) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Display Number
          </Typography>
          <TextField
            name="displayNumber"
            inputProps={{
              maxLength: 6,
            }}
            fullWidth
            placeholder="Display Number"
            value={question.properties.displayNumber || ""}
            onChange={handleDisplayNumber}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}
      {["number"].includes(question.type) && (
        <>
          <Stack gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              Measurement Unit
            </Typography>
            <TextField
              name="measurementUnit"
              fullWidth
              placeholder="Measurement Unit"
              value={question.properties.measurementUnit}
              onChange={(e) =>
                onChange("measurementUnit", e.target.value, true)
              }
              InputProps={{
                readOnly: isPreview,
              }}
            />
          </Stack>
        </>
      )}
      {["slider"].includes(question.type) && (
        <>
          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Min Label
              </Typography>
              <TextField
                name="minLabel"
                fullWidth
                placeholder="Min Label"
                value={question.properties.minLabel}
                onChange={(e) => onChange("minLabel", e.target.value, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>

            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Max Label
              </Typography>
              <TextField
                name="maxLabel"
                fullWidth
                placeholder="Max Label"
                value={question.properties.maxLabel}
                onChange={(e) => onChange("maxLabel", e.target.value, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>
          </Stack>
        </>
      )}
      {![
        "statement",
        "summary",
        "randomize",
        "eligibility",
        "repeated_measure",
      ].includes(question.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Helper Text
          </Typography>
          <TextField
            name="helperText"
            fullWidth
            multiline
            rows={2}
            placeholder="Helper Text"
            value={question.helperText || ""}
            onChange={(e) => onChange("helperText", e.target.value)}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}
      {/* main question allowRemark */}
      {isAllowRemark && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Remark
          </Typography>
          <TextField
            name="remarkText"
            fullWidth
            placeholder="Remark"
            value={question.properties.remarkText || ""}
            onChange={(e) => onChange("remarkText", e.target.value, true)}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}
      {displayMarkdown && (
        <MarkdownPreview
          field="label"
          isProp={false}
          close={closeMarkdown}
          isPreview={isPreview}
        />
      )}
    </Stack>
  );
};
